import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { InferResponseType } from 'hono/client';
import { Briefcase, Download, MoreVertical, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, buttonVariants } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '~/components/ui/empty';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText
} from '~/components/ui/input-group';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '~/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type { api } from '~/lib/api';
import { formatDate } from '~/utils/date';

type JobsResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs']['$get']
>;
type Job = JobsResponse['jobs'][number];

function getPageRange(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | 'ellipsis')[] = [];
  const addPage = (p: number): void => {
    if (pages[pages.length - 1] !== p) pages.push(p);
  };
  addPage(1);
  if (current > 3) pages.push('ellipsis');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) addPage(p);
  if (current < total - 2) pages.push('ellipsis');
  addPage(total);
  return pages;
}

type JobCardProps = {
  job: Job;
  orgSlug: string;
  deptSlug: string;
};

function JobCard({ job, orgSlug, deptSlug }: JobCardProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card
      className="cursor-pointer rounded-none border-b border-x-0 border-t-0 ring-0 transition-colors hover:bg-accent/30"
      onClick={() => {
        void navigate({
          to: '/org/$orgSlug/department/$deptSlug/jobs/$jobId',
          params: { orgSlug, deptSlug, jobId: job.id }
        });
      }}
    >
      <CardContent>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Tooltip>
              <TooltipTrigger className="w-full cursor-pointer truncate text-left">
                <span className="text-sm font-semibold text-foreground">{job.title}</span>
                {job.location && (
                  <span className="text-sm text-muted-foreground"> — {job.location}</span>
                )}
              </TooltipTrigger>
              <TooltipContent className="sm:hidden">
                {job.title}
                {job.location ? ` — ${job.location}` : ''}
              </TooltipContent>
            </Tooltip>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {t('jobs.completedPlaceholder', {
                completed: job.completedInterviewCount,
                total: job.applicationCount
              })}
            </p>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {job.createdBy.name}
              <span className="mx-1.5">·</span>
              {formatDate(job.createdAt)}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <MoreVertical className="size-4 text-muted-foreground" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

type JobsListProps = {
  orgSlug: string;
  deptSlug: string;
  jobs: Job[];
  total: number;
  page: number;
  limit: number;
  atsName: string | undefined;
};

export function JobsList({
  orgSlug,
  deptSlug,
  jobs,
  total,
  page,
  limit,
  atsName
}: JobsListProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({
    from: '/_authenticated/org/$orgSlug/department/$deptSlug/jobs/'
  });

  const [inputValue, setInputValue] = useState(search.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue !== search.search) {
        void navigate({
          to: '/org/$orgSlug/department/$deptSlug/jobs',
          params: { orgSlug, deptSlug },
          search: (prev) => ({ ...prev, search: inputValue, page: 1 })
        });
      }
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [inputValue, navigate, search.search, orgSlug, deptSlug]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  function setPage(next: number): void {
    void navigate({
      to: '/org/$orgSlug/department/$deptSlug/jobs',
      params: { orgSlug, deptSlug },
      search: (prev) => ({ ...prev, page: next })
    });
  }

  const searchBar = (
    <InputGroup className="w-full sm:max-w-64">
      <InputGroupAddon align="inline-start">
        <InputGroupText>
          <Search className="size-4" />
        </InputGroupText>
      </InputGroupAddon>
      <InputGroupInput
        className="!text-sm"
        placeholder={t('jobs.searchPlaceholder')}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
      />
    </InputGroup>
  );

  if (jobs.length === 0 && !search.search) {
    return (
      <div className="flex flex-col gap-4">
        {searchBar}
        <Empty className="py-16">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Briefcase />
            </EmptyMedia>
            <EmptyTitle>{t('jobs.emptyTitle')}</EmptyTitle>
            <EmptyDescription>{t('jobs.emptyDescription')}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className="flex items-center gap-2">
              <Link
                to={
                  atsName
                    ? '/org/$orgSlug/department/$deptSlug/jobs/import'
                    : '/org/$orgSlug/integrations'
                }
                params={{ orgSlug, deptSlug }}
                className={buttonVariants({ variant: 'outline' })}
              >
                <Download className="size-4" />
                {t('jobs.importFromAts', { atsName: atsName ?? 'ATS' })}
              </Link>
              <Link
                to="/org/$orgSlug/department/$deptSlug/jobs/new"
                params={{ orgSlug, deptSlug }}
                className={buttonVariants()}
              >
                <Plus className="size-4" />
                {t('jobs.newJob')}
              </Link>
            </div>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      {searchBar}
      {jobs.length === 0 ? (
        <Empty className="py-16">
          <EmptyHeader>
            <EmptyTitle>{t('jobs.emptySearchDescription')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto sm:hidden">
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} orgSlug={orgSlug} deptSlug={deptSlug} />
            ))}
          </div>
          <div className="hidden flex-1 min-h-0 overflow-y-auto sm:block">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                    {t('jobs.columns.name')}
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                    {t('jobs.columns.createdBy')}
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                    {t('jobs.columns.createdAt')}
                  </TableHead>
                  <TableHead className="w-12 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                    {t('jobs.columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow
                    key={job.id}
                    className="cursor-pointer"
                    onClick={() => {
                      void navigate({
                        to: '/org/$orgSlug/department/$deptSlug/jobs/$jobId',
                        params: { orgSlug, deptSlug, jobId: job.id }
                      });
                    }}
                  >
                    <TableCell className="py-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-normal text-foreground">
                          {job.title}
                          {job.location && (
                            <span className="text-muted-foreground/70"> – {job.location}</span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground/70">
                          {t('jobs.completedPlaceholder', {
                            completed: job.completedInterviewCount,
                            total: job.applicationCount
                          })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-sm text-foreground">
                      {job.createdBy.name}
                    </TableCell>
                    <TableCell className="py-3 text-sm text-muted-foreground">
                      {formatDate(job.createdAt)}
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <MoreVertical className="size-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => {
                  setPage(page - 1);
                }}
                aria-disabled={page <= 1}
                className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
            {getPageRange(page, totalPages).map((item, i) =>
              item === 'ellipsis' ? (
                <PaginationItem key={`ellipsis-${String(i)}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    isActive={item === page}
                    onClick={() => {
                      setPage(item);
                    }}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() => {
                  setPage(page + 1);
                }}
                aria-disabled={page >= totalPages}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
